import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import jsQR from "jsqr";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import { decodeShare } from "../../utils/shareLink";
import {
  ScanLine, Camera, CameraOff, AlertCircle, CheckCircle2, ArrowRight, ArrowUpRight,
  Clipboard, RefreshCcw, ShieldCheck, Sparkles, X, ChevronRight, Clock,
} from "lucide-react";

const RECENT_KEY = "tc_scan_recent_v1";
const MAX_RECENT = 5;

/* ------------------------------------------------------------------ */

function saveRecent(url) {
  try {
    const now = Date.now();
    const existing = JSON.parse(window.localStorage.getItem(RECENT_KEY) || "[]");
    const deduped = [{ url, scannedAt: now }, ...existing.filter((r) => r.url !== url)].slice(0, MAX_RECENT);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(deduped));
    return deduped;
  } catch {
    return [];
  }
}

function readRecent() {
  try {
    return JSON.parse(window.localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
}

function isLikelyCheckListUrl(s) {
  if (typeof s !== "string") return false;
  try {
    const u = new URL(s);
    if (!u.pathname.endsWith("/c") && u.pathname !== "/c") return false;
    return /[#?&]d=[A-Za-z0-9_-]+/.test(u.hash) || /[#?&]d=[A-Za-z0-9_-]+/.test(u.search);
  } catch {
    return false;
  }
}

function hashFromUrl(url) {
  try {
    const u = new URL(url);
    return u.hash || "";
  } catch {
    return "";
  }
}

/* ------------------------------------------------------------------ */

export default function ScanChecklist() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanningRef = useRef(false);
  const rafRef = useRef(null);

  const [stage, setStage] = useState("idle"); // 'idle' | 'starting' | 'scanning' | 'error' | 'success'
  const [errorMsg, setErrorMsg] = useState("");
  const [lastResult, setLastResult] = useState(null); // {url, ok, reason, meta}
  const [manualValue, setManualValue] = useState("");
  const [recent, setRecent] = useState([]);
  const [torch, setTorch] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);

  useEffect(() => {
    setRecent(readRecent());
  }, []);

  /* ---------- Camera lifecycle ---------- */
  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    detectorRef.current = null;
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleDecoded = useCallback(
    (text) => {
      if (!text) return;
      if (!isLikelyCheckListUrl(text)) {
        setLastResult({ ok: false, reason: "not-checklist", url: text });
        return;
      }
      const hash = hashFromUrl(text);
      const decoded = decodeShare(hash);
      const result = {
        url: text,
        ok: decoded.ok,
        reason: decoded.reason,
        meta: decoded.meta,
      };
      setLastResult(result);
      if (decoded.ok) {
        setStage("success");
        setRecent(saveRecent(text));
        stopCamera();
      }
    },
    [stopCamera]
  );

  const scanLoop = useCallback(() => {
    if (!scanningRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && video.readyState === video.HAVE_ENOUGH_DATA && canvas) {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w > 0 && h > 0) {
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, w, h);

        /* Prefer native BarcodeDetector when available */
        if (detectorRef.current) {
          detectorRef.current
            .detect(canvas)
            .then((codes) => {
              const code = codes && codes[0];
              if (code && code.rawValue) handleDecoded(code.rawValue);
            })
            .catch(() => { /* ignore frame failures */ });
        } else {
          try {
            const img = ctx.getImageData(0, 0, w, h);
            const code = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
            if (code && code.data) handleDecoded(code.data);
          } catch {
            /* ignore */
          }
        }
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [handleDecoded]);

  const startCamera = async () => {
    setErrorMsg("");
    setLastResult(null);
    setStage("starting");
    try {
      /* Prefer native BarcodeDetector where available */
      if (typeof window !== "undefined" && "BarcodeDetector" in window) {
        try {
          // eslint-disable-next-line no-undef
          detectorRef.current = new BarcodeDetector({ formats: ["qr_code"] });
        } catch {
          detectorRef.current = null;
        }
      }

      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      /* Torch support */
      const track = stream.getVideoTracks()[0];
      const caps = track?.getCapabilities?.() || {};
      setHasTorch(!!caps.torch);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      scanningRef.current = true;
      setStage("scanning");
      scanLoop();
    } catch (e) {
      setErrorMsg(e?.name === "NotAllowedError" ? "Camera permission denied." : e?.message || "Couldn't start the camera.");
      setStage("error");
    }
  };

  const toggleTorch = async () => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track || !hasTorch) return;
      await track.applyConstraints({ advanced: [{ torch: !torch }] });
      setTorch((t) => !t);
    } catch (e) {
      /* ignore */
    }
  };

  const handleOpenResult = () => {
    if (lastResult?.ok && lastResult.url) {
      navigate(`/c${hashFromUrl(lastResult.url)}`);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const v = manualValue.trim();
    if (!v) return;
    handleDecoded(v);
    if (isLikelyCheckListUrl(v)) {
      const hash = hashFromUrl(v);
      const decoded = decodeShare(hash);
      if (decoded.ok) navigate(`/c${hash}`);
    }
  };

  const clearRecent = () => {
    window.localStorage.removeItem(RECENT_KEY);
    setRecent([]);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="scan-checklist">
      <PageMeta
        title="Scan a Checklist | TRITAL Care®"
        description="Scan a clinician's TRITAL Care® shareable checklist QR code with your camera. For recruiters at career fairs, clinical conferences, and on-site interviews."
        canonical="https://www.tritalcare.com/c/scan"
      />
      <PublicNav />

      {/* HERO ------------------------------------------------------ */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="scan-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-12 lg:pt-20 lg:pb-14">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-6">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance/checklists" className="hover:text-trital-navy">Checklists</Link>
            <span className="mx-2 text-slate-300">/</span>
            <span className="text-slate-700">Scan</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <ScanLine className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                <span className="overline text-trital-navy">For recruiters</span>
              </div>
              <h1 className="mt-6 font-display font-black text-4xl lg:text-[52px] leading-[1.04] tracking-tight text-slate-900 text-balance">
                Scan. Open. <span className="text-trital-navy">Decide in 10 seconds.</span>
              </h1>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">
                Point your camera at a clinician's shareable-link QR code — at a career fair, clinical conference, or across a hiring-manager's desk. The aggregate skills checklist opens on your device. Zero email. Zero typing. Zero server-side storage.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 text-[12px] text-slate-600">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200">
                  <ShieldCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                  Camera stays on-device
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200">
                  <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
                  BarcodeDetector · jsQR fallback
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-5 lg:p-6 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
                <div className="relative">
                  <ViewFinder
                    stage={stage}
                    videoRef={videoRef}
                    canvasRef={canvasRef}
                    onStart={startCamera}
                    onStop={stopCamera}
                    errorMsg={errorMsg}
                    torch={torch}
                    hasTorch={hasTorch}
                    onToggleTorch={toggleTorch}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULT ---------------------------------------------------- */}
      {lastResult && (
        <section className="max-w-3xl mx-auto px-5 lg:px-8 py-8">
          <ResultBanner result={lastResult} onOpen={handleOpenResult} onDismiss={() => setLastResult(null)} />
        </section>
      )}

      {/* MANUAL PASTE FALLBACK ------------------------------------ */}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-6" data-testid="scan-manual">
        <div className="rounded-3xl bg-slate-50 border border-slate-200 p-6 lg:p-7">
          <div className="flex items-center gap-2">
            <Clipboard className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
            <div className="overline text-trital-red">Manual entry</div>
          </div>
          <h2 className="mt-2 font-display font-bold text-[18px] text-slate-900">No camera? Paste a link.</h2>
          <form onSubmit={handleManualSubmit} className="mt-4 flex gap-2">
            <input
              type="url"
              inputMode="url"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              placeholder="https://www.tritalcare.com/c#d=…"
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-[13px] text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-trital-navy/20 focus:border-trital-navy"
              aria-label="Shareable URL"
              data-testid="scan-manual-input"
            />
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep"
              data-testid="scan-manual-submit"
            >
              Open <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
          </form>
          <p className="mt-3 text-[12px] text-slate-500">
            We only accept `…/c#d=…` TRITAL Care® share links. Everything decodes client-side.
          </p>
        </div>
      </section>

      {/* RECENT SCANS --------------------------------------------- */}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-16" data-testid="scan-recent">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
            <h2 className="font-display font-bold text-[16px] text-slate-900">Recent scans</h2>
          </div>
          {recent.length > 0 && (
            <button
              onClick={clearRecent}
              className="text-[12px] font-semibold text-slate-500 hover:text-trital-red inline-flex items-center gap-1"
              data-testid="scan-recent-clear"
            >
              <RefreshCcw className="w-3 h-3" strokeWidth={1.8} /> Clear
            </button>
          )}
        </div>
        {recent.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-5 text-center text-[13px] text-slate-500">
            No recent scans on this device yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {recent.map((r, i) => (
              <li key={r.url + i} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                <div className="shrink-0 w-9 h-9 rounded-full bg-trital-medical-blue-soft flex items-center justify-center">
                  <ScanLine className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-mono text-slate-700 truncate" title={r.url}>{r.url}</div>
                  <div className="text-[11px] text-slate-500">{new Date(r.scannedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</div>
                </div>
                <button
                  onClick={() => navigate(`/c${hashFromUrl(r.url)}`)}
                  className="shrink-0 inline-flex items-center gap-1 px-3.5 py-2 rounded-full bg-white border border-slate-300 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
                  data-testid={`scan-recent-open-${i}`}
                >
                  Open <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Viewfinder sub-component                                            */
/* ------------------------------------------------------------------ */

function ViewFinder({ stage, videoRef, canvasRef, onStart, onStop, errorMsg, torch, hasTorch, onToggleTorch }) {
  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden aspect-[3/4] sm:aspect-video bg-slate-900" data-testid="scan-viewfinder">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          playsInline
          data-testid="scan-video"
        />
        <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

        {/* Reticle overlay */}
        {stage === "scanning" && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-black/30" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(70%,280px)] aspect-square">
              <div className="absolute inset-0 rounded-3xl ring-1 ring-white/30" />
              {/* corners */}
              <Corner pos="top-left" />
              <Corner pos="top-right" />
              <Corner pos="bottom-left" />
              <Corner pos="bottom-right" />
              <motion.div
                initial={{ top: 0 }}
                animate={{ top: ["0%", "100%", "0%"] }}
                transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
                className="absolute left-3 right-3 h-0.5 bg-trital-red/80 shadow-[0_0_12px_rgba(211,47,47,0.9)]"
              />
            </div>
          </div>
        )}

        {/* Idle state */}
        {stage === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-slate-900 text-white">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
              <Camera className="w-7 h-7" strokeWidth={1.8} />
            </div>
            <div className="mt-4 font-display font-bold text-lg">Camera idle</div>
            <p className="mt-1 text-[12.5px] text-white/70 max-w-xs">
              We'll ask for permission to use your camera. The video stream stays on your device.
            </p>
            <button
              onClick={onStart}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-trital-red text-white text-[13px] font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="scan-start-btn"
            >
              <Camera className="w-4 h-4" strokeWidth={1.8} /> Start camera
            </button>
          </div>
        )}

        {/* Starting */}
        {stage === "starting" && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-[13px]">
            Requesting camera…
          </div>
        )}

        {/* Error */}
        {stage === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-slate-900 text-white" data-testid="scan-error">
            <div className="w-14 h-14 rounded-2xl bg-red-500/20 text-red-200 flex items-center justify-center">
              <CameraOff className="w-7 h-7" strokeWidth={1.8} />
            </div>
            <div className="mt-4 font-display font-bold text-lg">Camera unavailable</div>
            <p className="mt-1 text-[12.5px] text-white/70 max-w-xs">{errorMsg || "Check browser permissions and try again."}</p>
            <button
              onClick={onStart}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-slate-900 text-[13px] font-semibold btn-lift hover:bg-slate-100"
              data-testid="scan-retry-btn"
            >
              <RefreshCcw className="w-4 h-4" strokeWidth={1.8} /> Try again
            </button>
          </div>
        )}
      </div>

      {/* Controls under viewfinder */}
      {stage === "scanning" && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[12px] text-slate-500 inline-flex items-center gap-1.5">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            Scanning…
          </div>
          <div className="flex gap-2">
            {hasTorch && (
              <button
                onClick={onToggleTorch}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-[12.5px] font-semibold ${
                  torch
                    ? "bg-trital-navy text-white border-trital-navy"
                    : "bg-white text-trital-navy border-slate-300 hover:bg-slate-50"
                }`}
                data-testid="scan-torch-toggle"
              >
                {torch ? "Torch off" : "Torch on"}
              </button>
            )}
            <button
              onClick={onStop}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-slate-300 text-[12.5px] font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="scan-stop-btn"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.8} /> Stop
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Corner({ pos }) {
  const base = "absolute w-6 h-6 border-white";
  const map = {
    "top-left": "top-0 left-0 border-t-2 border-l-2 rounded-tl-lg",
    "top-right": "top-0 right-0 border-t-2 border-r-2 rounded-tr-lg",
    "bottom-left": "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg",
    "bottom-right": "bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg",
  };
  return <div className={`${base} ${map[pos]}`} />;
}

/* ------------------------------------------------------------------ */

function ResultBanner({ result, onOpen, onDismiss }) {
  if (result.ok) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3" data-testid="scan-result-ok">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-[15px] text-emerald-900">Valid checklist link detected.</div>
          <div className="mt-1 text-[12.5px] text-emerald-800 font-mono truncate" title={result.url}>{result.url}</div>
        </div>
        <button
          onClick={onOpen}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-[12.5px] font-semibold btn-lift hover:bg-emerald-700"
          data-testid="scan-result-open"
        >
          Open <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
        </button>
      </div>
    );
  }

  let message = "We couldn't read this code.";
  if (result.reason === "expired") message = "This shareable link has expired.";
  else if (result.reason === "corrupted") message = "The QR code appears corrupted.";
  else if (result.reason === "missing") message = "The QR code is missing the checklist payload.";
  else if (result.reason === "not-checklist") message = "That isn't a TRITAL Care® share link.";

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="scan-result-err">
      <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
        <AlertCircle className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-[15px] text-amber-900">{message}</div>
        {result.url && (
          <div className="mt-1 text-[11.5px] text-amber-800 font-mono truncate" title={result.url}>{result.url}</div>
        )}
        <div className="mt-2 text-[11.5px] text-amber-800">
          Ask the clinician to regenerate a fresh link from their <Link to="/my-checklists" className="underline font-semibold">My Checklists</Link> page.
        </div>
      </div>
      <button
        onClick={onDismiss}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-amber-300 text-[12.5px] font-semibold text-amber-900 hover:bg-amber-100"
        data-testid="scan-result-dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}
